import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "../lib/api";
import { MOODS } from "../lib/moods";
import { useI18n } from "../context/I18nContext";
import { Loader } from "../components/ProtectedRoute";
import { MoodBadge } from "../components/MoodTagEditor";

const dayKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const MoodStats = ({ monthEntries }) => {
  const { t } = useI18n();
  const counts = MOODS.map((m) => ({ ...m, n: monthEntries.filter((e) => e.mood === m.key).length })).filter((m) => m.n);
  const max = Math.max(1, ...counts.map((c) => c.n));
  if (!counts.length) return <p className="text-sm text-[#8E9BB4]" data-testid="calendar-stats-empty">{t("cal_no_mood")}</p>;
  return (
    <div className="space-y-3" data-testid="calendar-stats">
      {counts.map(({ key, icon: Icon, color, n }) => (
        <div key={key} className="flex items-center gap-3 text-sm" data-testid={`calendar-stat-${key}`}>
          <span className="w-32 flex items-center gap-2" style={{ color }}><Icon size={14} /> {t(`mood_${key}`)}</span>
          <div className="flex-1 h-2 rounded-full bg-white/5 overflow-hidden">
            <div className="h-full rounded-full transition-[width] duration-700" style={{ width: `${(n / max) * 100}%`, background: color }} />
          </div>
          <span className="w-6 text-right text-[#8E9BB4] font-mono text-xs">{n}</span>
        </div>
      ))}
    </div>
  );
};

export default function MoodCalendar() {
  const { t, lang } = useI18n();
  const [entries, setEntries] = useState(null);
  const [cursor, setCursor] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get("/entries").then((r) => setEntries(r.data)).catch(() => setEntries([]));
  }, []);

  const byDay = useMemo(() => {
    const map = {};
    (entries || []).forEach((e) => { (map[dayKey(new Date(e.created_at))] ||= []).push(e); });
    return map;
  }, [entries]);

  if (entries === null) return <Loader />;

  const year = cursor.getFullYear(), month = cursor.getMonth();
  const offset = (new Date(year, month, 1).getDay() + 6) % 7;
  const days = new Date(year, month + 1, 0).getDate();
  const cells = [...Array(offset).fill(null), ...Array.from({ length: days }, (_, i) => new Date(year, month, i + 1))];
  const locale = lang === "fr" ? "fr-FR" : "en-GB";
  const weekdays = Array.from({ length: 7 }, (_, i) => new Date(2024, 0, 1 + i).toLocaleDateString(locale, { weekday: "short" }));
  const monthEntries = entries.filter((e) => { const d = new Date(e.created_at); return d.getFullYear() === year && d.getMonth() === month; });
  const todayKey = dayKey(new Date());
  const move = (n) => { setCursor(new Date(year, month + n, 1)); setSelected(null); };

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-14 sm:py-20 fade-up">
      <p className="label-gold mb-3">{t("cal_kicker")}</p>
      <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#F4F6FA] font-semibold mb-10">{t("cal_title")}</h1>
      <div className="grid lg:grid-cols-[1.6fr_1fr] gap-8">
        <div className="glass-card p-5 sm:p-8">
          <div className="flex items-center justify-between mb-6">
            <button data-testid="calendar-prev-btn" onClick={() => move(-1)} className="w-9 h-9 rounded-full border border-white/10 grid place-items-center text-[#C2C9DB] hover:border-[#E5C158]/50"><ChevronLeft size={16} /></button>
            <h2 className="font-serif text-2xl sm:text-3xl text-[#F4F6FA] capitalize" data-testid="calendar-month-label">{cursor.toLocaleDateString(locale, { month: "long", year: "numeric" })}</h2>
            <button data-testid="calendar-next-btn" onClick={() => move(1)} className="w-9 h-9 rounded-full border border-white/10 grid place-items-center text-[#C2C9DB] hover:border-[#E5C158]/50"><ChevronRight size={16} /></button>
          </div>
          <div className="grid grid-cols-7 gap-1.5 sm:gap-2 text-center">
            {weekdays.map((w) => <div key={w} className="text-[10px] sm:text-xs uppercase tracking-wider text-[#8E9BB4] pb-2">{w}</div>)}
            {cells.map((d, i) => {
              if (!d) return <div key={`e${i}`} />;
              const k = dayKey(d);
              const list = byDay[k] || [];
              const mood = MOODS.find((m) => m.key === list.find((e) => e.mood)?.mood);
              const isSel = selected === k;
              return (
                <button
                  key={k}
                  data-testid={`calendar-day-${k}`}
                  onClick={() => setSelected(isSel ? null : k)}
                  className={`aspect-square rounded-xl flex flex-col items-center justify-center gap-1 border text-sm transition-transform duration-200 hover:scale-105 ${isSel ? "ring-2 ring-[#E5C158]" : ""} ${k === todayKey ? "border-[#E5C158]/60" : "border-white/5"}`}
                  style={{ background: mood ? `${mood.color}33` : list.length ? "rgba(229,193,88,0.06)" : "rgba(255,255,255,0.02)" }}
                >
                  <span className={mood ? "text-[#F4F6FA]" : "text-[#8E9BB4]"}>{d.getDate()}</span>
                  {mood ? <mood.icon size={14} style={{ color: mood.color }} /> : list.length ? <span className="w-1.5 h-1.5 rounded-full bg-[#E5C158]/70" /> : null}
                </button>
              );
            })}
          </div>
          <div className="flex flex-wrap gap-3 mt-6" data-testid="calendar-legend">
            {MOODS.map(({ key, icon: Icon, color }) => (
              <span key={key} className="flex items-center gap-1.5 text-xs" style={{ color }}><Icon size={12} /> {t(`mood_${key}`)}</span>
            ))}
          </div>
        </div>
        <div className="space-y-6">
          <div className="gold-card p-6">
            <p className="label-gold mb-4">{t("cal_month_moods")}</p>
            <MoodStats monthEntries={monthEntries} />
          </div>
          {selected && (
            <div className="gold-card p-6 fade-up" data-testid="calendar-day-panel">
              <p className="label-gold mb-4">{new Date(`${selected}T12:00:00`).toLocaleDateString(locale, { weekday: "long", day: "numeric", month: "long" })}</p>
              {(byDay[selected] || []).length === 0 ? (
                <p className="text-sm text-[#8E9BB4]">{t("cal_no_entry")}</p>
              ) : (
                <div className="space-y-3">
                  {byDay[selected].map((e) => (
                    <Link key={e.id} to={`/journal/${e.id}`} data-testid={`calendar-entry-${e.id}`} className="block rounded-xl border border-white/5 hover:border-[#E5C158]/40 p-3 transition-colors">
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <span className="font-serif text-lg text-[#F4F6FA] line-clamp-1">{e.title}</span>
                        <MoodBadge mood={e.mood} />
                      </div>
                      <p className="text-xs text-[#8E9BB4] line-clamp-1">{e.transcript}</p>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
